import { fillText } from 'components/invoiceImport/invoiceImportText';

/** Fired by the "Tout relancer" confirmation once a run is to be followed (JBWEB-1283): `{ type, runId }`. */
export const INVOICE_REMINDER_RUN_STARTED = 'invoice_reminder_run_started';

/** The modal of a manual reminder (JBWEB-1283 T5), opened with `{ number }`. */
export const INVOICE_REMINDER_MODAL = 'invoice_reminder';

/**
 * The confirmation of a move to or out of litigation (JBWEB-1284), opened
 * with `{ number, action: 'start' | 'end' }`. On a success it fires
 * INVOICE_REMINDER_CHANGED, as the manual reminder modal does.
 */
export const INVOICE_LITIGATION_MODAL = 'invoice_litigation';

/**
 * Fired by the manual reminder modal (JBWEB-1283 T5) once the invoice's
 * reminders changed: a reminder sent, or a refusal saying the invoice is no
 * longer reminded. `{ type, number }`: the tab, its counter and the invoice's
 * detail read themselves again.
 */
export const INVOICE_REMINDER_CHANGED = 'invoice_reminder_changed';

/** What the api accepts for a manual reminder's message, trimmed (REMINDER_MESSAGE_MAX_LENGTH). */
export const REMINDER_MESSAGE_MAX_LENGTH = 5000;

/** The sub-tabs of the reminders, in the order of the screen, as the api names them. */
export const REMINDER_TABS = ['todo', 'upcoming', 'litigation', 'settled'];

/** The steps of the api calendar (utils/invoiceReminderSchedule.js), in rank order. */
export const REMINDER_LEVELS = ['before_due', 'first', 'second', 'third'];

/**
 * Every text of the reminders tab and of its "Tout relancer" confirmation, as
 * the `invoiceReminders` space of fr.js holds them (a test keeps the two
 * equal): the default read when a language has no such text.
 */
export const INVOICE_REMINDERS_TEXTS = {
  tabCount: '{count} à faire',
  remindAll: 'Tout relancer',
  remindAllRunning: 'Relance en cours…',
  cards: {
    toDo: 'À faire',
    late: 'En retard',
    litigation: 'Contentieux',
    invoicesOne: '{count} facture',
    invoicesMany: '{count} factures',
    litigationValue: '{count} · {amount}',
    unknown: '—',
  },
  tabs: {
    label: 'Relances',
    todo: 'À faire',
    upcoming: 'À venir',
    litigation: 'Contentieux',
    settled: 'Réglées',
  },
  columns: {
    number: 'N°',
    company: 'Entreprise',
    remaining: 'Reste à payer',
    late: 'Retard',
    step: 'À envoyer',
    passage: 'Passage',
    status: 'Statut',
  },
  row: {
    invoiceOf: 'Facture {month}',
    noCompany: 'Sans entreprise',
    lateDaysOne: '{days} jour',
    lateDaysMany: '{days} jours',
    lateSince: 'depuis le {date}',
    notLate: 'Pas en retard',
    dueOn: 'échéance le {date}',
    nextOn: 'le {date}',
    remind: 'Relancer',
    open: 'Ouvrir',
    moreActions: 'Plus d’actions sur la facture {number}',
    suspend: 'Suspendre les relances',
    resume: 'Reprendre les relances',
  },
  badges: {
    before_due: 'Rappel',
    first: 'Relance 1/3',
    second: 'Relance 2/3',
    third: 'Relance 3/3',
    exhausted: 'Relances épuisées',
    noRecipient: 'Sans destinataire',
    suspended: 'Suspendue',
    uncertain: 'Envoi incertain',
    litigation: 'Contentieux',
  },
  badgeHints: {
    noRecipient:
      'Aucun contact « Relances » dans l’entreprise : personne ne recevrait la relance.',
    suspended: 'Les relances automatiques de cette facture sont arrêtées.',
    uncertain:
      'Un envoi est resté en cours plus de 10 minutes : vérifiez l’historique avant de relancer.',
  },
  suspension: {
    suspended: 'Relances automatiques suspendues pour la facture N°{number}.',
    resumedNext:
      'Relances automatiques reprises pour la facture N°{number} : prochaine relance le {date}.',
    resumedNone:
      'Relances automatiques reprises pour la facture N°{number} : aucune relance automatique n’est prévue.',
    errors: {
      INVOICE_NOT_REMINDABLE:
        'Cette facture n’est plus relancée (réglée, annulée ou en contentieux) : la liste est relue.',
      INVOICE_NOT_FOUND: 'Cette facture est introuvable.',
      generic: 'Le changement n’a pas pu être enregistré, réessayez.',
    },
  },
  list: {
    loading: 'Chargement des relances…',
    loadFailed: 'La liste des relances n’a pas pu être chargée.',
    retry: 'Réessayer',
    showingRange: 'Affichage de {start}-{end} sur {total} résultats',
    showingAll: 'Affichage de {total} résultats',
    totalLate: 'Total en retard : {amount}',
    totalOwed: 'Total restant dû : {amount}',
    totalLitigation: 'Total en contentieux : {amount}',
    filteredEmptyTitle: 'Aucune facture ne correspond à cette recherche.',
    clearSearch: 'Effacer la recherche',
    empty: {
      todo: {
        title: 'Aucune facture à relancer',
        text: 'Les factures dont une relance est due apparaîtront ici.',
      },
      upcoming: {
        title: 'Aucune relance à venir',
        text: 'Les factures non réglées dont la prochaine relance n’est pas encore due apparaîtront ici.',
      },
      litigation: {
        title: 'Aucune facture en contentieux',
        text: 'Les factures confiées au contentieux apparaîtront ici.',
      },
      settled: {
        title: 'Aucune facture réglée après relance',
        text: 'Les factures payées après au moins une relance apparaîtront ici.',
      },
    },
  },
  remindAllModal: {
    title: 'Tout relancer',
    loading: 'Calcul des factures à relancer…',
    loadFailed: 'Les factures à relancer n’ont pas pu être comptées.',
    retryLoad: 'Réessayer',
    intro:
      'Chaque facture d’« À faire » reçoit maintenant sa relance due, PDF joint, envoyée à ses contacts « Relances ».',
    countOne: '{count} facture sera relancée :',
    countMany: '{count} factures seront relancées :',
    nothing: 'Aucune facture d’« À faire » n’a de relance à envoyer.',
    step: '{label} : {count}',
    suspendedRule: 'Les factures suspendues ne sont pas relancées.',
    suspendedOne: '{count} facture suspendue ne sera pas relancée.',
    suspendedMany: '{count} factures suspendues ne seront pas relancées.',
    exhaustedOne:
      '{count} facture aux relances épuisées n’a plus de relance à envoyer.',
    exhaustedMany:
      '{count} factures aux relances épuisées n’ont plus de relance à envoyer.',
    triedRule:
      'Une relance déjà tentée (en échec ou « Envoi incertain ») n’est pas renvoyée : relancez ces factures une par une.',
    cancel: 'Annuler',
    confirm: 'Tout relancer',
    starting: 'Lancement…',
    errors: {
      REMINDER_RUN_ALREADY_RUNNING: 'Un passage de relances est déjà en cours.',
      BILLING_MAIL_DISABLED:
        'L’envoi de mails est coupé sur cet environnement.',
      BILLING_MAIL_TEMPLATE_MISSING:
        'Le modèle du mail de relance manque sur cet environnement.',
      INVOICE_NUMBERS_INVALID:
        'La liste des factures à relancer n’est pas valide.',
      generic: 'Le passage n’a pas pu être lancé, réessayez.',
    },
  },
  run: {
    alreadyRunning:
      'Un passage de relances est déjà en cours : son compte rendu s’affichera à la fin.',
    done: 'Tout relancer : passage terminé',
    failed: 'Tout relancer : le passage s’est arrêté en échec',
    skipped: 'Tout relancer : le passage n’a pas eu lieu',
    counters:
      'Envoyées : {sent} · échouées : {failed} · sans destinataire : {noRecipient} · déjà réglées : {settled} · déjà réservées : {alreadyClaimed}',
    lost: 'Le suivi du passage a été perdu : relisez la liste dans un moment.',
  },
  reminderModal: {
    title: 'Envoyer une relance',
    close: 'Fermer',
    summary: {
      number: 'N°{number}',
      remaining: 'reste à payer {amount}',
      lateOne: '{days} jour de retard',
      lateMany: '{days} jours de retard',
      dueOn: 'échéance le {date}',
    },
    loading: 'Préparation de la relance…',
    reload: 'Réessayer',
    recipientsLabel: 'Destinataires',
    recipients: {
      none: 'Aucun contact de facturation pour cette entreprise.',
      member: 'Membre',
      pendingMember: 'Invitation en attente',
      external: 'Externe',
      unnamed: 'Contact sans nom',
    },
    levelLabel: 'Modèle de relance',
    levels: {
      before_due: 'Rappel · Échéance proche',
      first: 'Relance 1/3 · Facture impayée',
      second: 'Relance 2/3 · Second rappel',
      third: 'Relance 3/3 · Mise en demeure amiable',
    },
    levelLoading: 'Chargement du modèle…',
    levelFailed:
      'Ce modèle n’a pas pu être chargé : le précédent est gardé, réessayez.',
    messageLabel: 'Message',
    messageTooLong: 'Le message dépasse {max} caractères ({count} saisis).',
    attachment: 'La facture PDF est jointe automatiquement.',
    suspend: 'Suspendre les relances automatiques pour cette facture',
    nextAutomatic: 'Prochaine relance automatique prévue le {date}.',
    alreadySuspended:
      'Les relances automatiques de cette facture sont déjà suspendues.',
    cancel: 'Annuler',
    send: 'Envoyer la relance',
    sending: 'Envoi en cours…',
    retrySend: 'Réessayer l’envoi',
    frozenNotice:
      'La première tentative a peut-être abouti : la relance est figée. Réessayez l’envoi à l’identique ou fermez la fenêtre.',
    sent: 'Relance envoyée pour la facture N°{number}.',
    sentSuspended:
      'Relance envoyée pour la facture N°{number} ; ses relances automatiques sont suspendues.',
    replayed:
      'Cette relance de la facture N°{number} avait déjà été envoyée : rien n’a été renvoyé.',
    draftErrors: {
      INVOICE_NOT_REMINDABLE:
        'Cette facture n’est pas relancée : réglée, annulée, avoir, sans échéance ou non importée.',
      INVOICE_IN_LITIGATION:
        'Cette facture est en contentieux : sortez-la d’abord du contentieux pour la relancer.',
      INVOICE_NOT_FOUND: 'Cette facture est introuvable.',
      generic: 'La relance n’a pas pu être préparée.',
    },
    errors: {
      INVOICE_SETTLED:
        'La facture a été réglée entre-temps : aucune relance n’est partie.',
      INVOICE_IN_LITIGATION:
        'Cette facture est en contentieux : sortez-la d’abord du contentieux pour la relancer.',
      INVOICE_NOT_REMINDABLE:
        'Cette facture n’est plus relancée (réglée, annulée ou sans échéance) : aucune relance n’est partie.',
      SEND_IN_PROGRESS:
        'Cette relance est encore en cours d’envoi : patientez, puis réessayez. Rien ne partira deux fois.',
      LEVEL_INVALID: 'Ce modèle de relance n’est pas reconnu.',
      MESSAGE_INVALID: 'Le message doit compter de 1 à 5 000 caractères.',
      RECIPIENTS_INVALID:
        'Un destinataire coché n’est plus un contact de facturation de l’entreprise : fermez et rouvrez la fenêtre.',
      REQUEST_ID_INVALID:
        'Cet envoi n’a pas pu être identifié : fermez et rouvrez la fenêtre.',
      INVOICE_NOT_FOUND: 'Cette facture est introuvable.',
      MAIL_SEND_FAILED:
        'Le mail de relance n’a pas pu partir : aucune relance n’a été envoyée. Vous pouvez réessayer, ce sera un nouvel envoi.',
      PENNYLANE_READ_FAILED:
        'Le statut de la facture n’a pas pu être relu dans Pennylane : rien n’est parti. Réessayez dans un moment.',
      BILLING_MAIL_DISABLED:
        'L’envoi de mails est coupé sur cet environnement.',
      BILLING_MAIL_TEMPLATE_MISSING:
        'Le modèle du mail de relance manque sur cet environnement.',
      generic: 'L’envoi a échoué, réessayez.',
      network:
        'Le serveur n’a pas répondu. Réessayez : une relance déjà partie ne partira pas deux fois.',
    },
  },
  detail: {
    remind: 'Envoyer une relance',
  },
  litigation: {
    bannerTitleOne: '{count} facture en contentieux',
    bannerTitleMany: '{count} factures en contentieux',
    bannerText:
      'Les relances automatiques sont arrêtées. À transmettre pour recouvrement.',
    lastSent: '{label} le {date}',
    noneSent: 'Aucune relance envoyée',
    since: 'Depuis le {date}',
    start: 'Passer en contentieux',
    end: 'Sortir du contentieux',
    startModal: {
      title: 'Passer en contentieux',
      text: 'Passer la facture N°{number} en contentieux ? Les relances automatiques et manuelles s’arrêtent jusqu’à sa sortie.',
      cancel: 'Annuler',
      confirm: 'Passer en contentieux',
      pending: 'Passage en cours…',
      done: 'La facture N°{number} est passée en contentieux.',
    },
    endModal: {
      title: 'Sortir du contentieux',
      text: 'Sortir la facture N°{number} du contentieux ? Elle revient dans les relances, sans rattrapage : les relances déjà échues repassent dans « À faire » et attendent un admin.',
      cancel: 'Annuler',
      confirm: 'Sortir du contentieux',
      pending: 'Sortie en cours…',
      doneNext:
        'La facture N°{number} est sortie du contentieux. Prochaine relance automatique prévue le {date}.',
      doneNone:
        'La facture N°{number} est sortie du contentieux. Aucune relance automatique prévue.',
    },
    errors: {
      INVOICE_NUMBER_INVALID: 'Ce numéro de facture n’est pas valide.',
      INVOICE_NOT_FOUND: 'Cette facture est introuvable.',
      ALREADY_IN_LITIGATION: 'Cette facture est déjà en contentieux.',
      INVOICE_SETTLED:
        'Cette facture est réglée ou annulée : elle ne peut pas passer en contentieux.',
      INVOICE_NOT_LATE:
        'Cette facture n’est pas en retard : elle ne peut pas passer en contentieux.',
      NOT_IN_LITIGATION: 'Cette facture n’est plus en contentieux.',
      INVOICE_LITIGATION_FAILED:
        'Le serveur n’a pas pu enregistrer le changement : rien n’a changé, réessayez.',
      forbidden: 'Vous n’avez pas accès aux factures.',
      generic: 'Le changement n’a pas pu être enregistré, réessayez.',
      network:
        'Pas de réponse du serveur : le changement a peut-être été enregistré. La liste est relue.',
    },
  },
};

const defaultTextAt = (path) =>
  path
    .split('.')
    .reduce((value, key) => value?.[key], INVOICE_REMINDERS_TEXTS) ?? '';

/** `text("row.remind")`: the text of the language, else the French default. */
export const invoiceRemindersText = (language) => (path, values) => {
  const value = language.getValue(
    `invoiceReminders.${path}`,
    defaultTextAt(path),
  );
  return values ? fillText(value, values) : value;
};

/** `{count} facture` or `{count} factures`, from the `<prefix>One` / `<prefix>Many` pair. */
export const countText = (text, prefix, count, values = {}) =>
  text(`${prefix}${count === 1 ? 'One' : 'Many'}`, { count, ...values });

const euros = new Intl.NumberFormat('fr-FR', {
  style: 'currency',
  currency: 'EUR',
});

/** "2 477,60 €". */
export const formatReminderEuros = (amount) =>
  euros.format(Number.isFinite(amount) ? amount : 0);

// The api dates days at 00:00 UTC: read from the string, never through a time zone.
const dayParts = (value) => {
  const iso =
    value instanceof Date && !Number.isNaN(value.getTime())
      ? value.toISOString()
      : String(value ?? '');
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  return match ? { year: match[1], month: match[2], day: match[3] } : null;
};

/** "14/07" for "2026-07-14T00:00:00.000Z". */
export const formatDayMonth = (value) => {
  const parts = dayParts(value);
  return parts ? `${parts.day}/${parts.month}` : null;
};

/** "14/07/2026" for "2026-07-14T00:00:00.000Z". */
export const formatFullDay = (value) => {
  const parts = dayParts(value);
  return parts ? `${parts.day}/${parts.month}/${parts.year}` : null;
};

// A moment (a reminder sent, a move to litigation) is a timestamp, not a day
// at 00:00 UTC: it reads in the admin's time zone, or an evening in
// Martinique would show the next day.
const momentParts = (value) => {
  if (value == null || value === '') return null;
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  const pad = (number) => String(number).padStart(2, '0');
  return {
    year: String(date.getFullYear()),
    month: pad(date.getMonth() + 1),
    day: pad(date.getDate()),
  };
};

/** "29/07" for a moment, in the local time zone. */
export const formatMomentDayMonth = (value) => {
  const parts = momentParts(value);
  return parts ? `${parts.day}/${parts.month}` : null;
};

/** "29/07/2026" for a moment, in the local time zone. */
export const formatMomentFullDay = (value) => {
  const parts = momentParts(value);
  return parts ? `${parts.day}/${parts.month}/${parts.year}` : null;
};

/** The refusals of the litigation routes (api docs/facturation/technique.md § 59). */
export const LITIGATION_ERROR_CODES = [
  'INVOICE_NUMBER_INVALID',
  'INVOICE_NOT_FOUND',
  'ALREADY_IN_LITIGATION',
  'INVOICE_SETTLED',
  'INVOICE_NOT_LATE',
  'NOT_IN_LITIGATION',
  'INVOICE_LITIGATION_FAILED',
];

/**
 * The text of a refused move to or out of litigation (JBWEB-1284): an
 * uncertain outcome when no answer came back, else the code's own, else the
 * 403's, else a generic one.
 */
export const litigationErrorText = (text, error) => {
  if (!error?.response) return text('litigation.errors.network');
  const code = error.response.data?.errorCode;
  if (LITIGATION_ERROR_CODES.includes(code)) {
    return text(`litigation.errors.${code}`);
  }
  if (error?.response?.status === 403) {
    return text('litigation.errors.forbidden');
  }
  return text('litigation.errors.generic');
};
